import React, { useEffect, useState, useCallback } from "react";
import Feature from "./Feature";
import { useDispatch, useSelector } from "react-redux";
import { SelectSiteFunnelDBData, SelectSiteUserJourneyUserWeekLabels, setFunnelViewDBData, SelectSiteUsersDBData, setSiteUsersDBData, setCurrentSelectedWeek, SelectCurrentSelectedWeek, setJourneyActiveDateRange, SelectJourneyActiveDateRange } from "../../HeatmapXUserSegmentSlice";
import Filter from "./Filter";
import { FirebaseDataService } from "../../../../services/firebase-data.service";
import { selectLogInUsername } from "../../../../common/authenticateDashboard/authSlice";
import DateRangeFilter from "../../../../components/Graphs/DateRangeFilter/DateRangeFilter";

const formatDateYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseAvgTime = (timeStr) => {
  if (!timeStr) return 0;
  if (typeof timeStr === 'number') return timeStr;
  let seconds = 0;
  const str = timeStr.toString().trim();
  const parts = str.split(' ');
  parts.forEach(part => {
    if (part.endsWith('h')) {
      seconds += parseInt(part) * 3600;
    } else if (part.endsWith('m')) {
      seconds += parseInt(part) * 60;
    } else if (part.endsWith('s')) {
      seconds += parseInt(part);
    }
  });
  return seconds;
};

const formatAvgTime = (seconds) => {
  if (isNaN(seconds) || seconds <= 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  let res = [];
  if (h > 0) res.push(`${h}h`);
  if (m > 0) res.push(`${m}m`);
  if (s > 0 || res.length === 0) res.push(`${s}s`);
  return res.join(' ');
};

const mergeJourneyTrees = (trees) => {
  if (!trees || trees.length === 0) return null;
  if (trees.length === 1) return trees[0];

  const mergeNodes = (nodes) => {
    if (!nodes || nodes.length === 0) return null;
    if (nodes.length === 1) return nodes[0];

    const merged = { ...nodes[0] };
    
    merged.totalUsers = nodes.reduce((sum, n) => sum + (n.totalUsers || 0), 0);
    merged.clicks = nodes.reduce((sum, n) => sum + (n.clicks || 0), 0);
    merged.bookBtnClicks = nodes.reduce((sum, n) => sum + (n.bookBtnClicks || 0), 0);
    merged.value = nodes.reduce((sum, n) => sum + (n.value || 0), 0);
    
    let totalTimeUsers = 0;
    let totalTimeSum = 0;
    nodes.forEach(n => {
      const parsedSeconds = parseAvgTime(n.avgTime);
      if (parsedSeconds > 0 && (n.totalUsers > 0)) {
        totalTimeSum += (parsedSeconds * n.totalUsers);
        totalTimeUsers += n.totalUsers;
      }
    });
    const avgSeconds = totalTimeUsers > 0 ? Math.round(totalTimeSum / totalTimeUsers) : 0;
    merged.avgTime = formatAvgTime(avgSeconds);
    
    merged.scroll = Math.max(...nodes.map(n => n.scroll || 0));

    merged.devices = {};
    merged.userTypes = {};
    merged.userSources = {};
    
    nodes.forEach(n => {
      Object.entries(n.devices || {}).forEach(([k, v]) => {
        merged.devices[k] = (merged.devices[k] || 0) + v;
      });
      Object.entries(n.userTypes || {}).forEach(([k, v]) => {
        merged.userTypes[k] = (merged.userTypes[k] || 0) + v;
      });
      Object.entries(n.userSources || {}).forEach(([k, v]) => {
        merged.userSources[k] = (merged.userSources[k] || 0) + v;
      });
    });

    const referrersMap = new Map();
    nodes.forEach(n => {
      (n.referrers || []).forEach(([url, count]) => {
        referrersMap.set(url, (referrersMap.get(url) || 0) + count);
      });
    });
    merged.referrers = Array.from(referrersMap.entries()).sort((a, b) => b[1] - a[1]);

    const childrenMap = {};
    nodes.forEach(n => {
      (n.children || []).forEach(child => {
        if (!childrenMap[child.name]) {
          childrenMap[child.name] = [];
        }
        childrenMap[child.name].push(child);
      });
    });
    merged.children = Object.values(childrenMap).map(mergeNodes).filter(Boolean);

    return merged;
  };

  return mergeNodes(trees);
};

const CF_Filters = {
  userType: {
    title: "Audience Type",
    options: [
      { id: "all", title: "All Users" },
      { id: "first_time", title: "First Time" },
      { id: "returning", title: "Returning" },
    ],
    value: "",
  },
  userSource: {
    title: "User Traffic Source",
    options: [
      { id: "all", title: "All Sources" },
      { id: "paid", title: "Paid" },
      { id: "organic", title: "Organic" },
    ],
    value: "",
  },
  referrers: {
    title: "Referrers",
    value: [],
  },
  utmParams: {
    title: "UTM Params",
    value: [], // array of { key, value }
  },
};

const CustomerJourney = () => {
  const dispatch = useDispatch();
  const UserFunnelDBData = useSelector(SelectSiteFunnelDBData);
  const SiteUsersWeekLabels = useSelector(SelectSiteUserJourneyUserWeekLabels);
  const SiteUsersDBData = useSelector(SelectSiteUsersDBData);
  const siteFunnelDBData = useSelector(SelectSiteFunnelDBData);
  const [weekLabels, setWeekLabels] = useState([]);
  const [currentWeek, setCurrentWeek] = useState("");
  const [currentWeekData, setCurrentWeekData] = useState(null);
  const [filterObj, setFilterObj] = useState({});
  const firebaseDataService = new FirebaseDataService();
  const sessionUser = useSelector(selectLogInUsername);
  const user = sessionUser?.split("@")[0];
  const persistedWeekLabel = useSelector(SelectCurrentSelectedWeek);
  const persistedDateRange = useSelector(SelectJourneyActiveDateRange); // { start, end } or null

  // Restore from Redux on mount
  const [activeDateRange, setActiveDateRange] = useState(persistedDateRange);
  const [initialStartDate] = useState(
    persistedDateRange?.start ? new Date(persistedDateRange.start) : null
  );
  const [initialEndDate] = useState(
    persistedDateRange?.end ? new Date(persistedDateRange.end) : null
  );

  useEffect(() => {
    if (Object.keys(UserFunnelDBData).length) {
      const weeks = Object.keys(UserFunnelDBData);
      // Restore persisted week if available, else default to latest
      const weekToUse = persistedWeekLabel && UserFunnelDBData[persistedWeekLabel]
        ? persistedWeekLabel
        : weeks[0];
      setCurrentWeek(weekToUse);
      setCurrentWeekData(UserFunnelDBData[weekToUse]);
    }
  }, []);

  useEffect(() => {
    if (SiteUsersWeekLabels.length) {
      setWeekLabels(SiteUsersWeekLabels);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(UserFunnelDBData).length > 0 || Object.keys(filterObj).length >= 0) {
      const filteredData = applyFilterOnData(filterObj);
      setCurrentWeekData(filteredData);
    }
  }, [filterObj, activeDateRange, currentWeek, UserFunnelDBData]);

  useEffect(() => {
    const fetchData = async () => {
      let [yy, mm, dd] = currentWeek.split("-");

      mm = String(mm).padStart(2, '0');
      dd = String(dd).padStart(2, '0');

      const cachedData = siteFunnelDBData?.[currentWeek];
      if (cachedData && Object.keys(cachedData).length > 0) {
        console.log(`Using Redux cached data for ${currentWeek}`);
        setCurrentWeek(currentWeek);
        setCurrentWeekData(cachedData);
        return; // No Firebase call needed!
      }

      console.log("user,yy, mm, dd", user,yy, mm, dd);
      const data = await firebaseDataService.getDataForDate(user, yy, mm, dd); 
      console.log("userJorneySelectedWeekData----->", data);
      console.log("data.journey", data?.journey);

      if (!data || Object.keys(data.journey).length === 0) {
        console.log("No data available for the selected week.");
        return;
      }
      setCurrentWeekData(data.journey ?? null);
      dispatch(setSiteUsersDBData({
        ...SiteUsersDBData,
        [currentWeek]: data.heatmap ?? {}
      }));
      dispatch(setFunnelViewDBData({
        ...siteFunnelDBData,
        [currentWeek] : data.journey ?? {}
      }));
    };
    
    if(currentWeek!==""){
      fetchData();
      dispatch(setCurrentSelectedWeek(currentWeek));
    }
  }, [currentWeek]);

  const fetchMissingDatesForRange = useCallback(async (datesToFetch) => {
    let newHeatmapData = { ...SiteUsersDBData };
    let newJourneyData = { ...siteFunnelDBData };
    
    for (const dateLabel of datesToFetch) {
      const [yy, mm, dd] = dateLabel.split("-");
      try {
        const data = await firebaseDataService.getDataForDate(user, yy, mm, dd);
        if (data) {
          newHeatmapData[dateLabel] = data.heatmap ?? {};
          newJourneyData[dateLabel] = data.journey ?? {};
        }
      } catch (error) {
        console.error('Error fetching data for date:', dateLabel, error);
      }
    }
    
    dispatch(setSiteUsersDBData(newHeatmapData));
    dispatch(setFunnelViewDBData(newJourneyData));
  }, [SiteUsersDBData, siteFunnelDBData, user, dispatch]);

  const handleDateRangeChange = useCallback(async ({ startDate, endDate, filteredIndices }) => {
    if (startDate && endDate && filteredIndices) {
      const datesToFetch = filteredIndices
        .map(i => weekLabels[i])
        .filter(dateLabel => !UserFunnelDBData[dateLabel]);

      if (datesToFetch.length > 0) {
        await fetchMissingDatesForRange(datesToFetch);
      }

      const newRange = {
        start: formatDateYMD(startDate),
        end: formatDateYMD(endDate),
      };
      setActiveDateRange(newRange);
      dispatch(setJourneyActiveDateRange(newRange)); // persist in Redux
    } else {
      setActiveDateRange(null);
      dispatch(setJourneyActiveDateRange(null)); // clear from Redux
    }
  }, [weekLabels, UserFunnelDBData, fetchMissingDatesForRange, dispatch]);

  const getBaseData = () => {
    if (activeDateRange) {
      const start = new Date(activeDateRange.start);
      const end = new Date(activeDateRange.end);
      end.setHours(23, 59, 59, 999);
      
      const treesToMerge = [];
      Object.entries(UserFunnelDBData).forEach(([label, treeData]) => {
        const labelDate = new Date(label);
        if (labelDate >= start && labelDate <= end && treeData && Object.keys(treeData).length > 0) {
          treesToMerge.push(treeData);
        }
      });
      return mergeJourneyTrees(treesToMerge);
    } else {
      return UserFunnelDBData[currentWeek];
    }
  };

  const applyFilterOnData = (filterObj) => {
    const root = getBaseData();
    if (!root || Object.keys(root).length === 0) return null;

    const {
      userType = "",
      userSource = "",
      referrers = [],
      utmParams = [],
    } = filterObj;

    const filterTree = (node) => {
      if (!node) return null;

      // User Type filter
      const matchesUserType =
        !userType || userType === "all" || (node.userTypes?.[userType] > 0);

      // User Source filter
      const matchesUserSource =
        !userSource || userSource === "all" || (node.userSources?.[userSource] > 0);

      // Referrers filter
      const matchesReferrers =
        !referrers.length ||
        referrers.some((ref) =>
          node.referrers?.some(([refUrl]) =>
            refUrl.toLowerCase().includes(ref.toLowerCase())
          )
        );

      // UTM Params filter
      const matchesUTM =
        !utmParams.length ||
        utmParams.every(({ key, value }) =>
          node.URL?.includes(`${key}=${value}`)
        );

      const children = (node.children || [])
        .map(filterTree)
        .filter(Boolean); // Keep only valid subtrees

      const isMatch =
        matchesUserType && matchesUserSource && matchesReferrers && matchesUTM;

      if (isMatch || children.length > 0) {
        return {
          ...node,
          children,
        };
      }

      return null;
    };

    const filteredData = filterTree(root);
    console.log("Filtered Tree:", filteredData);

    return filteredData || null;
  };

  return (
    <div>
      <div className="flex justify-between items-center mt-[-32px]">
        <Filter
          CF_Filters={CF_Filters}
          CF_FilterObj={filterObj}
          setCF_FilterObj={setFilterObj}
        />
        <div className="relative z-10">
          <DateRangeFilter
            weeks={weekLabels}
            onFilterChange={handleDateRangeChange}
            initialStartDate={initialStartDate}
            initialEndDate={initialEndDate}
          />
        </div>
      </div>

      {currentWeekData ? (
        <Feature data={currentWeekData} currentWeek={currentWeek} activeDateRange={activeDateRange} />
      ) : (
        <div className="text-center text-gray-500 mt-8">
          No data matches the selected filters.
        </div>
      )}
    </div>
  );
};

export default CustomerJourney;